import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { eq } from "drizzle-orm";
import {
	AlertTriangle,
	ArrowLeft,
	ArrowUpDown,
	ChevronDown,
	ChevronUp,
	Download,
	Loader2,
	Pencil,
	Plus,
	Search,
	Sparkles,
	Trash2,
	Upload,
	X,
} from "lucide-react";
import { Fragment, useId, useMemo, useRef, useState } from "react";
import ConfirmDialog, { useConfirm } from "@/components/ConfirmDialog";
import { getDb } from "@/db";
import { cars, events } from "@/db/schema";
import { getSession } from "@/lib/auth-session";
import { escapeCsvField, parseCsvLine } from "@/lib/csv";

// ── Server functions (unchanged) ────────────────────────────────

async function requireAdmin() {
	const session = await getSession();
	if (!session?.user) throw new Error("Unauthorized");
	const role = (session.user as { role?: string }).role;
	if (role !== "admin" && role !== "operator") throw new Error("Forbidden");
}

const getEventWithCars = createServerFn({ method: "GET" })
	.inputValidator((eventId: string) => eventId)
	.handler(async ({ data: eventId }) => {
		const db = getDb();
		const event = await db
			.select()
			.from(events)
			.where(eq(events.id, Number(eventId)))
			.get();
		if (!event) throw new Error("Event not found");

		const eventCars = await db
			.select()
			.from(cars)
			.where(eq(cars.eventId, event.id));
		return { event, cars: eventCars };
	});

const addCar = createServerFn({ method: "POST" })
	.inputValidator(
		(data: {
			eventId: number;
			driverName: string;
			carName: string;
			weightOz: string;
			carNumber: number;
			division: string;
		}) => data,
	)
	.handler(async ({ data }) => {
		await requireAdmin();
		const result = await getDb()
			.insert(cars)
			.values({
				eventId: data.eventId,
				driverName: data.driverName,
				carName: data.carName || "",
				weightOz: data.weightOz ? Number(data.weightOz) : null,
				carNumber: data.carNumber,
				division: data.division || null,
			})
			.returning();
		return result[0];
	});

const updateCar = createServerFn({ method: "POST" })
	.inputValidator(
		(data: {
			carId: number;
			driverName: string;
			carName: string;
			carNumber: number;
			weightOz: string;
			division: string;
		}) => data,
	)
	.handler(async ({ data }) => {
		await requireAdmin();
		await getDb()
			.update(cars)
			.set({
				driverName: data.driverName,
				carName: data.carName || "",
				carNumber: data.carNumber,
				weightOz: data.weightOz ? Number(data.weightOz) : null,
				division: data.division || null,
				updatedAt: new Date(),
			})
			.where(eq(cars.id, data.carId));
	});

const updateCarPhoto = createServerFn({ method: "POST" })
	.inputValidator((data: { carId: number; photoUrl: string }) => data)
	.handler(async ({ data }) => {
		await requireAdmin();
		await getDb()
			.update(cars)
			.set({ photoUrl: data.photoUrl, updatedAt: new Date() })
			.where(eq(cars.id, data.carId));
	});

const deleteCar = createServerFn({ method: "POST" })
	.inputValidator((carId: number) => carId)
	.handler(async ({ data: carId }) => {
		await requireAdmin();
		await getDb().delete(cars).where(eq(cars.id, carId));
	});

export const Route = createFileRoute("/admin/events/$eventId/cars")({
	loader: ({ params }) => getEventWithCars({ data: params.eventId }),
	component: AdminCarsPage,
});

// ── Types ───────────────────────────────────────────────────────

type CarRow = Awaited<ReturnType<typeof getEventWithCars>>["cars"][number];
type SortKey = "carNumber" | "driverName" | "carName" | "division";

type ModalState =
	| { type: "none" }
	| { type: "add" }
	| { type: "edit"; car: CarRow }
	| { type: "import-preview"; rows: ImportRow[] };

interface ImportRow {
	driverName: string;
	carName: string;
	carNumber: number | null;
	weightOz: number | null;
	division: string | null;
	checkedIn: boolean;
	matchedCarId: number | null;
	status: "new" | "update" | "unchanged";
	changedFields: Set<string>;
	warnings: string[];
}

// ── Main page component ─────────────────────────────────────────

function AdminCarsPage() {
	const { event, cars: carList } = Route.useLoaderData();
	const router = useRouter();
	const [confirmFn, confirmDialog] = useConfirm();
	const divisions: string[] = (() => {
		try {
			return JSON.parse(event.divisions);
		} catch {
			return [];
		}
	})();
	const hasDivisions = divisions.length > 0;

	// Modal / table state
	const [modal, setModal] = useState<ModalState>({ type: "none" });
	const [sortKey, setSortKey] = useState<SortKey>("carNumber");
	const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
	const [search, setSearch] = useState("");

	// Avatar / loading state
	const [generatingAvatarId, setGeneratingAvatarId] = useState<number | null>(
		null,
	);
	const [avatarConfirmCarId, setAvatarConfirmCarId] = useState<number | null>(
		null,
	);
	const [formLoading, setFormLoading] = useState(false);
	const [formError, setFormError] = useState<string | null>(null);
	const [importing, setImporting] = useState(false);

	const fileInputRef = useRef<HTMLInputElement>(null);

	// Stats
	const stats = useMemo(() => {
		const total = carList.length;
		const checkedIn = carList.filter((c) => c.checkedIn).length;
		const overweight = carList.filter(
			(c) => c.weightOz != null && c.weightOz > event.maxWeightOz,
		).length;
		const divisionCounts: Record<string, { total: number; checkedIn: number }> =
			{};
		if (divisions.length > 0) {
			for (const div of divisions) {
				divisionCounts[div] = { total: 0, checkedIn: 0 };
			}
			for (const car of carList) {
				const key = car.division || "Unassigned";
				if (!divisionCounts[key]) {
					divisionCounts[key] = { total: 0, checkedIn: 0 };
				}
				divisionCounts[key].total++;
				if (car.checkedIn) divisionCounts[key].checkedIn++;
			}
		}
		return { total, checkedIn, overweight, divisionCounts };
	}, [carList, divisions, event.maxWeightOz]);

	// Filtered + sorted car list
	const filteredCars = useMemo(() => {
		const q = search.toLowerCase();
		let list = carList;
		if (q) {
			list = list.filter(
				(c) =>
					c.driverName.toLowerCase().includes(q) ||
					(c.carName ?? "").toLowerCase().includes(q) ||
					String(c.carNumber).includes(q) ||
					(c.division ?? "").toLowerCase().includes(q),
			);
		}
		return [...list].sort((a, b) => {
			let av: string | number;
			let bv: string | number;
			switch (sortKey) {
				case "carNumber":
					av = a.carNumber;
					bv = b.carNumber;
					break;
				case "driverName":
					av = a.driverName.toLowerCase();
					bv = b.driverName.toLowerCase();
					break;
				case "carName":
					av = (a.carName ?? "").toLowerCase();
					bv = (b.carName ?? "").toLowerCase();
					break;
				case "division":
					av = (a.division ?? "").toLowerCase();
					bv = (b.division ?? "").toLowerCase();
					break;
			}
			if (av < bv) return sortDir === "asc" ? -1 : 1;
			if (av > bv) return sortDir === "asc" ? 1 : -1;
			return 0;
		});
	}, [carList, search, sortKey, sortDir]);

	const TABLE_LIMIT = 100;
	const displayedCars = filteredCars.slice(0, TABLE_LIMIT);
	const hasMore = filteredCars.length > TABLE_LIMIT;

	// ── Helpers ──

	function toggleSort(key: SortKey) {
		if (sortKey === key) {
			setSortDir((d) => (d === "asc" ? "desc" : "asc"));
		} else {
			setSortKey(key);
			setSortDir("asc");
		}
	}

	function SortIcon({ col }: { col: SortKey }) {
		if (sortKey !== col) return <ArrowUpDown size={12} className="opacity-30" />;
		return sortDir === "asc" ? (
			<ChevronUp size={12} />
		) : (
			<ChevronDown size={12} />
		);
	}

	async function uploadPhoto(carId: number, file: File) {
		const formData = new FormData();
		formData.append("file", file);
		formData.append("carId", String(carId));
		const res = await fetch("/api/upload", { method: "POST", body: formData });
		if (!res.ok) throw new Error("Upload failed");
		const { url } = (await res.json()) as { url: string };
		await updateCarPhoto({ data: { carId, photoUrl: url } });
		return url;
	}

	async function handleFormSubmit(
		data: {
			driverName: string;
			carName: string;
			carNumber: number;
			weightOz: string;
			division: string;
			photoFile: File | null;
		},
		editCarId?: number,
	) {
		setFormLoading(true);
		setFormError(null);
		try {
			if (editCarId) {
				// Edit existing car
				await updateCar({
					data: {
						carId: editCarId,
						driverName: data.driverName,
						carName: data.carName,
						carNumber: data.carNumber,
						weightOz: data.weightOz,
						division: data.division,
					},
				});

				let hasNewPhoto = false;
				if (data.photoFile) {
					await uploadPhoto(editCarId, data.photoFile);
					hasNewPhoto = true;
				}

				const existingCar = carList.find((c) => c.id === editCarId);
				const hasPhoto = hasNewPhoto || !!existingCar?.photoUrl;
				const carName = data.carName;
				if ((carName || hasPhoto) && !existingCar?.avatarUrl) {
					fetch("/api/generate-avatar", {
						method: "POST",
						headers: { "Content-Type": "application/json" },
						body: JSON.stringify({
							carId: editCarId,
							carName: carName || existingCar?.carName,
						}),
					});
				}
			} else {
				// Add new car
				const car = await addCar({
					data: {
						eventId: event.id,
						driverName: data.driverName,
						carName: data.carName,
						weightOz: data.weightOz,
						carNumber: data.carNumber,
						division: data.division,
					},
				});

				if (data.photoFile && car) {
					await uploadPhoto(car.id, data.photoFile);
				}

				if (car?.carName) {
					fetch("/api/generate-avatar", {
						method: "POST",
						headers: { "Content-Type": "application/json" },
						body: JSON.stringify({ carId: car.id, carName: car.carName }),
					});
				}
			}

			setModal({ type: "none" });
			await router.invalidate();
		} catch (err) {
			setFormError(err instanceof Error ? err.message : "Operation failed");
		} finally {
			setFormLoading(false);
		}
	}

	async function handleDelete(carId: number) {
		const ok = await confirmFn({
			title: "Delete Car",
			description:
				"Delete this car and all associated race data? This cannot be undone.",
			confirmLabel: "Delete",
			variant: "destructive",
		});
		if (!ok) return;
		await deleteCar({ data: carId });
		await router.invalidate();
	}

	function handleGenerateAvatar(
		carId: number,
		carName: string | null,
		hasAvatar: boolean,
	) {
		if (!carName) return;
		if (hasAvatar) {
			setAvatarConfirmCarId(carId);
			return;
		}
		doGenerateAvatar(carId, carName);
	}

	async function doGenerateAvatar(carId: number, carName: string) {
		setGeneratingAvatarId(carId);
		const res = await fetch("/api/generate-avatar", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ carId, carName }),
		});
		if (!res.ok) console.error("Avatar generation failed:", res.status);
		setGeneratingAvatarId(null);
		await router.invalidate();
	}

	// ── CSV Export ──

	function handleExport() {
		const unit = event.weightUnit;
		const headers = [
			"Car Number",
			"Driver Name",
			"Car Name",
			`Weight (${unit})`,
			...(hasDivisions ? ["Division"] : []),
			"Checked In",
		];
		const rows = carList.map((c) => [
			escapeCsvField(String(c.carNumber)),
			escapeCsvField(c.driverName),
			escapeCsvField(c.carName ?? ""),
			escapeCsvField(c.weightOz != null ? String(c.weightOz) : ""),
			...(hasDivisions ? [escapeCsvField(c.division ?? "")] : []),
			escapeCsvField(c.checkedIn ? "Yes" : "No"),
		]);
		const csv = [headers.map(escapeCsvField).join(","), ...rows.map((r) => r.join(","))].join(
			"\n",
		);
		const blob = new Blob([csv], { type: "text/csv" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `${event.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-cars.csv`;
		a.click();
		URL.revokeObjectURL(url);
	}

	// ── CSV Import (client-side parse + match) ──

	function handleImportFile(e: React.ChangeEvent<HTMLInputElement>) {
		const file = e.target.files?.[0];
		if (!file) return;

		const reader = new FileReader();
		reader.onload = () => {
			const text = reader.result as string;
			const lines = text.split(/\r?\n/).filter((l) => l.trim());
			if (lines.length < 2) return;

			const header = parseCsvLine(lines[0]).map((h) =>
				h.toLowerCase().replace(/[^a-z0-9]/g, ""),
			);

			const findCol = (...names: string[]) =>
				header.findIndex((h) => names.some((n) => h === n || h.includes(n)));

			const numberCol = findCol("carnumber", "number", "num");
			const carNameCol = header.findIndex(
				(h, i) =>
					i !== numberCol &&
					["carname", "car"].some((n) => h === n || h.includes(n)),
			);
			const weightCol = findCol("weight");
			const divisionCol = findCol("division", "div", "den", "rank", "group");
			const checkedInCol = findCol("checkedin", "checked");
			const driverCol = header.findIndex(
				(h, i) =>
					i !== carNameCol &&
					i !== numberCol &&
					["driver", "drivername", "name", "racer", "scout"].some(
						(n) => h === n || h.includes(n),
					),
			);

			if (driverCol === -1) return;

			const rows: ImportRow[] = [];
			const matchedIds = new Set<number>();
			for (let i = 1; i < lines.length; i++) {
				const fields = parseCsvLine(lines[i]);
				const driverName = fields[driverCol]?.trim();
				if (!driverName) continue;

				let carName =
					carNameCol !== -1 ? fields[carNameCol]?.trim() || "" : "";
				const carNumberRaw =
					numberCol !== -1
						? Number.parseInt(fields[numberCol]?.trim(), 10)
						: Number.NaN;
				const carNumber =
					!Number.isNaN(carNumberRaw) && carNumberRaw > 0
						? carNumberRaw
						: null;

				const weightStr = weightCol !== -1 ? fields[weightCol]?.trim() : "";
				let weightOz =
					weightStr && !Number.isNaN(Number.parseFloat(weightStr))
						? Number.parseFloat(weightStr)
						: null;

				let division =
					divisionCol !== -1 ? fields[divisionCol]?.trim() || null : null;

				const checkedInRaw =
					checkedInCol !== -1 ? fields[checkedInCol]?.trim().toLowerCase() : "";
				let checkedIn = ["yes", "true", "1", "y"].includes(checkedInRaw);

				// Match against existing cars
				let matchedCar: CarRow | undefined;
				if (carNumber != null) {
					matchedCar = carList.find(
						(c) => c.carNumber === carNumber && !matchedIds.has(c.id),
					);
				}
				if (!matchedCar) {
					matchedCar = carList.find(
						(c) =>
							!matchedIds.has(c.id) &&
							c.driverName.trim().toLowerCase() ===
								driverName.trim().toLowerCase(),
					);
				}
				if (matchedCar) matchedIds.add(matchedCar.id);

				// For columns absent from CSV, preserve existing car values
				// so we don't show false diffs or overwrite data on upsert
				if (matchedCar) {
					if (carNameCol === -1) carName = matchedCar.carName ?? "";
					if (weightCol === -1) weightOz = matchedCar.weightOz ?? null;
					if (divisionCol === -1) division = matchedCar.division ?? null;
					if (checkedInCol === -1) checkedIn = matchedCar.checkedIn;
				}

				let status: ImportRow["status"] = "new";
				const changedFields = new Set<string>();
				if (matchedCar) {
					// Normalize comparisons: treat null and "" as equivalent for strings
					const normStr = (v: string | null | undefined) => (v ?? "").trim();
					const normNum = (v: number | null | undefined) =>
						v == null ? null : v;

					if (normStr(matchedCar.driverName) !== normStr(driverName))
						changedFields.add("driverName");
					if (normStr(matchedCar.carName) !== normStr(carName))
						changedFields.add("carName");
					if (
						carNumber != null &&
						matchedCar.carNumber !== carNumber
					)
						changedFields.add("carNumber");
					if (normNum(matchedCar.weightOz) !== normNum(weightOz))
						changedFields.add("weightOz");
					if (normStr(matchedCar.division) !== normStr(division))
						changedFields.add("division");
					if (matchedCar.checkedIn !== checkedIn)
						changedFields.add("checkedIn");

					status = changedFields.size > 0 ? "update" : "unchanged";
				}

				const warnings: string[] = [];
				if (
					division &&
					divisions.length > 0 &&
					!divisions.some(
						(d) => d.toLowerCase() === division.toLowerCase(),
					)
				) {
					warnings.push(
						`Division "${division}" is not configured for this event`,
					);
				}

				rows.push({
					driverName,
					carName,
					carNumber,
					weightOz,
					division,
					checkedIn,
					matchedCarId: matchedCar?.id ?? null,
					status,
					changedFields,
					warnings,
				});
			}

			setModal({ type: "import-preview", rows });
		};
		reader.readAsText(file);
		if (fileInputRef.current) fileInputRef.current.value = "";
	}

	async function handleApplyImport(rows: ImportRow[]) {
		setImporting(true);
		try {
			const formData = new FormData();
			formData.append("mode", "upsert");
			formData.append("eventId", String(event.id));
			formData.append("rows", JSON.stringify(rows));
			const res = await fetch("/api/import-roster", {
				method: "POST",
				body: formData,
			});
			if (!res.ok) {
				const err = (await res.json()) as { error?: string };
				throw new Error(err.error || "Import failed");
			}
			const result = (await res.json()) as {
				inserted?: number;
				updated?: number;
				errors?: string[];
			};
			if (result.errors && result.errors.length > 0) {
				alert(
					`Import completed with errors:\n\n${result.errors.join("\n")}`,
				);
			}
			setModal({ type: "none" });
			await router.invalidate();
		} catch (err) {
			console.error("Import failed:", err);
			alert(err instanceof Error ? err.message : "Import failed");
		} finally {
			setImporting(false);
		}
	}

	// ── Render ──

	return (
		<div className="min-h-screen bg-background p-6">
			{confirmDialog}
			<div className="max-w-5xl mx-auto">
				<Link
					to="/admin/events/$eventId"
					params={{ eventId: String(event.id) }}
					className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground text-sm mb-4 transition-colors"
				>
					<ArrowLeft size={16} />
					Back to {event.name}
				</Link>

				<div className="flex items-center justify-between mb-6">
					<h1 className="text-4xl text-foreground">Cars</h1>
					<span className="text-muted-foreground text-sm">
						{carList.length} car{carList.length !== 1 ? "s" : ""}
					</span>
				</div>

				{/* Toolbar */}
				<div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-4">
					{/* Search */}
					<div className="relative flex-1">
						<Search
							size={16}
							className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
						/>
						<input
							type="text"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder="Search cars..."
							className="w-full pl-9 pr-4 py-2 text-sm"
						/>
					</div>

					{/* Actions */}
					<div className="flex items-center gap-2">
						<a
							href={`/api/import-template?eventId=${event.id}`}
							className="inline-flex items-center gap-1.5 px-3 py-2 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-lg transition-colors text-sm font-bold"
							title="Download CSV template"
						>
							<Download size={14} />
							Template
						</a>
						<input
							ref={fileInputRef}
							type="file"
							accept=".csv,text/csv"
							className="hidden"
							onChange={handleImportFile}
						/>
						<button
							type="button"
							onClick={() => fileInputRef.current?.click()}
							className="inline-flex items-center gap-1.5 px-3 py-2 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-lg transition-colors text-sm font-bold"
							title="Import cars from CSV"
						>
							<Upload size={14} />
							Import
						</button>
						<button
							type="button"
							onClick={handleExport}
							disabled={carList.length === 0}
							className="inline-flex items-center gap-1.5 px-3 py-2 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-lg transition-colors text-sm font-bold disabled:opacity-50"
							title="Export cars to CSV"
						>
							<Download size={14} />
							Export
						</button>
						<button
							type="button"
							onClick={() => {
								setFormError(null);
								setModal({ type: "add" });
							}}
							className="inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-accent text-primary-foreground font-bold rounded-lg transition-colors uppercase text-sm"
						>
							<Plus size={18} />
							Add Car
						</button>
					</div>
				</div>

				{/* Stats */}
				{carList.length > 0 && (
					<div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
						<div className="bg-card rounded-xl px-4 py-3">
							<p className="text-xs text-muted-foreground uppercase tracking-wider font-bold">
								Registered
							</p>
							<p className="text-2xl font-bold text-foreground">
								{stats.total}
							</p>
						</div>
						<div className="bg-card rounded-xl px-4 py-3">
							<p className="text-xs text-muted-foreground uppercase tracking-wider font-bold">
								Checked In
							</p>
							<p className="text-2xl font-bold text-foreground">
								{stats.checkedIn}
								<span className="text-sm font-normal text-muted-foreground ml-1">
									/ {stats.total}
								</span>
							</p>
						</div>
						{stats.overweight > 0 && (
							<div className="bg-card rounded-xl px-4 py-3">
								<p className="text-xs text-muted-foreground uppercase tracking-wider font-bold">
									Overweight
								</p>
								<p className="text-2xl font-bold text-destructive">
									{stats.overweight}
								</p>
							</div>
						)}
						{hasDivisions &&
							Object.entries(stats.divisionCounts).map(([div, counts]) => (
								<div key={div} className="bg-card rounded-xl px-4 py-3">
									<p className="text-xs text-muted-foreground uppercase tracking-wider font-bold">
										{div}
									</p>
									<p className="text-2xl font-bold text-foreground">
										{counts.total}
										<span className="text-sm font-normal text-muted-foreground ml-1">
											({counts.checkedIn} in)
										</span>
									</p>
								</div>
							))}
					</div>
				)}

				{/* Table */}
				{filteredCars.length === 0 ? (
					<div className="bg-card rounded-xl p-12 text-center">
						<p className="text-foreground text-lg mb-4">
							{carList.length === 0
								? "No cars registered yet"
								: "No cars match your search"}
						</p>
						{carList.length === 0 && (
							<button
								type="button"
								onClick={() => {
									setFormError(null);
									setModal({ type: "add" });
								}}
								className="inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-accent text-primary-foreground font-bold rounded-lg transition-colors uppercase text-sm"
							>
								<Plus size={18} />
								Add Car
							</button>
						)}
					</div>
				) : (
					<>
					<div className="overflow-x-auto rounded-xl border border-border">
						<table className="w-full text-sm">
							<thead>
								<tr className="bg-card border-b border-border">
									<SortHeader col="carNumber" label="#" />
									<th className="px-3 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">
										Avatar
									</th>
									<SortHeader col="driverName" label="Driver" />
									<SortHeader col="carName" label="Car Name" />
									<th className="px-3 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">
										Weight
									</th>
									{hasDivisions && (
										<SortHeader col="division" label="Division" />
									)}
									<th className="px-3 py-3 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">
										Checked In
									</th>
									<th className="px-3 py-3 text-right text-xs font-bold text-muted-foreground uppercase tracking-wider">
										Actions
									</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-border">
								{displayedCars.map((car) => (
									<tr
										key={car.id}
										className="bg-card/50 hover:bg-card transition-colors"
									>
										<td className="px-3 py-3">
											<span className="inline-flex items-center gap-0.5 text-xs font-bold text-primary bg-primary/10 rounded px-1.5 py-0.5">
												#{car.carNumber}
											</span>
										</td>
										<td className="px-3 py-3">
											{car.avatarUrl || car.photoUrl ? (
												<img
													src={(car.avatarUrl || car.photoUrl) as string}
													alt={car.carName ?? "Car"}
													className="w-8 h-8 rounded-lg object-cover"
												/>
											) : (
												<div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center text-muted-foreground text-xs font-bold">
													{car.carName?.charAt(0) ?? "#"}
												</div>
											)}
										</td>
										<td className="px-3 py-3 text-foreground">
											{car.driverName}
										</td>
										<td className="px-3 py-3 text-foreground">
											{car.carName || (
												<span className="text-muted-foreground">—</span>
											)}
										</td>
										<td className="px-3 py-3">
											{car.weightOz != null ? (
												<span
													className={`flex items-center gap-1 ${
														car.weightOz > event.maxWeightOz
															? "text-destructive font-bold"
															: "text-foreground"
													}`}
												>
													{car.weightOz > event.maxWeightOz && (
														<AlertTriangle size={14} />
													)}
													{car.weightOz} {event.weightUnit}
												</span>
											) : (
												<span className="text-muted-foreground">—</span>
											)}
										</td>
										{hasDivisions && (
											<td className="px-3 py-3">
												{car.division ? (
													<span className="inline-flex items-center px-1.5 py-0.5 bg-primary/10 text-primary text-xs font-bold rounded">
														{car.division}
													</span>
												) : (
													<span className="text-muted-foreground">—</span>
												)}
											</td>
										)}
										<td className="px-3 py-3">
											{car.checkedIn ? (
												<span className="text-success font-bold text-xs">
													Yes
												</span>
											) : (
												<span className="text-muted-foreground text-xs">
													No
												</span>
											)}
										</td>
										<td className="px-3 py-3">
											<div className="flex items-center justify-end gap-1">
												<button
													type="button"
													onClick={() => {
														setFormError(null);
														setModal({ type: "edit", car });
													}}
													title="Edit car"
													className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
												>
													<Pencil size={14} />
												</button>
												<button
													type="button"
													onClick={() =>
														handleGenerateAvatar(
															car.id,
															car.carName,
															!!car.avatarUrl,
														)
													}
													disabled={generatingAvatarId === car.id}
													title={
														car.avatarUrl
															? "Regenerate avatar"
															: "Generate avatar"
													}
													className="p-1.5 text-accent hover:text-primary hover:bg-muted rounded-lg transition-colors disabled:opacity-50"
												>
													{generatingAvatarId === car.id ? (
														<Loader2
															size={14}
															className="animate-spin"
														/>
													) : (
														<Sparkles size={14} />
													)}
												</button>
												<button
													type="button"
													onClick={() => handleDelete(car.id)}
													title="Delete car"
													className="p-1.5 text-destructive hover:text-destructive/80 hover:bg-muted rounded-lg transition-colors"
												>
													<Trash2 size={14} />
												</button>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
					{hasMore && (
						<p className="text-center text-sm text-muted-foreground py-3">
							Showing {TABLE_LIMIT} of {filteredCars.length} cars. Use search to narrow results.
						</p>
					)}
					</>
				)}
			</div>

			{/* Modals */}
			{(modal.type === "add" || modal.type === "edit") && (
				<CarFormModal
					mode={modal.type}
					car={modal.type === "edit" ? modal.car : undefined}
					divisions={divisions}
					eventId={event.id}
					weightUnit={event.weightUnit}
					loading={formLoading}
					error={formError}
					onClose={() => setModal({ type: "none" })}
					onSubmit={handleFormSubmit}
				/>
			)}

			{modal.type === "import-preview" && (
				<ImportPreviewModal
					rows={modal.rows}
					hasDivisions={hasDivisions}
					importing={importing}
					onClose={() => setModal({ type: "none" })}
					onApply={handleApplyImport}
				/>
			)}

			{/* Avatar regeneration confirm */}
			<ConfirmDialog
				open={avatarConfirmCarId !== null}
				onOpenChange={(open) => {
					if (!open) setAvatarConfirmCarId(null);
				}}
				title="Regenerate Avatar"
				description="This car already has an AI avatar. Generate a new one? This will replace the existing avatar."
				confirmLabel="Regenerate"
				onConfirm={() => {
					const car = carList.find((c) => c.id === avatarConfirmCarId);
					if (car?.carName) doGenerateAvatar(car.id, car.carName);
				}}
			/>
		</div>
	);

	// Sortable table header helper (must be inside component for closure access)
	function SortHeader({ col, label }: { col: SortKey; label: string }) {
		return (
			<th className="px-3 py-3 text-left">
				<button
					type="button"
					onClick={() => toggleSort(col)}
					className="inline-flex items-center gap-1 text-xs font-bold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors"
				>
					{label}
					<SortIcon col={col} />
				</button>
			</th>
		);
	}
}

// ── Car form modal ──────────────────────────────────────────────

function CarFormModal({
	mode,
	car,
	divisions,
	eventId,
	weightUnit,
	loading,
	error,
	onClose,
	onSubmit,
}: {
	mode: "add" | "edit";
	car?: CarRow;
	divisions: string[];
	eventId: number;
	weightUnit: string;
	loading: boolean;
	error: string | null;
	onClose: () => void;
	onSubmit: (
		data: {
			driverName: string;
			carName: string;
			carNumber: number;
			weightOz: string;
			division: string;
			photoFile: File | null;
		},
		editCarId?: number,
	) => void;
}) {
	const formId = useId();
	const [photoFile, setPhotoFile] = useState<File | null>(null);
	const hasDivisions = divisions.length > 0;

	function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		const form = new FormData(e.currentTarget);
		onSubmit(
			{
				driverName: form.get("driverName") as string,
				carName: (form.get("carName") as string) || "",
				carNumber: Number(form.get("carNumber")),
				weightOz: (form.get("weightOz") as string) || "",
				division: (form.get("division") as string) || "",
				photoFile,
			},
			car?.id,
		);
	}

	return (
		<>
			<div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
			<div className="fixed inset-0 z-50 flex items-center justify-center p-4">
				<div className="bg-card rounded-xl shadow-2xl w-full max-w-md">
					<div className="flex items-center justify-between px-5 py-4 border-b border-border">
						<h2 className="text-lg font-bold text-foreground">
							{mode === "add" ? "Add Car" : "Edit Car"}
						</h2>
						<button
							type="button"
							onClick={onClose}
							className="p-1 hover:bg-secondary rounded-lg transition-colors"
						>
							<X size={18} />
						</button>
					</div>
					<form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
						{/* Current avatar/photo when editing */}
						{mode === "edit" && car && (car.avatarUrl || car.photoUrl) && (
							<div className="flex items-center gap-3">
								<img
									src={(car.avatarUrl || car.photoUrl) as string}
									alt={car.carName ?? "Car"}
									className="w-16 h-16 rounded-lg object-cover"
								/>
								<span className="text-xs text-muted-foreground">
									Current {car.avatarUrl ? "avatar" : "photo"}
								</span>
							</div>
						)}

						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
							<div>
								<label
									htmlFor={`${formId}-driver`}
									className="block text-sm mb-1"
								>
									Driver Name
								</label>
								<input
									id={`${formId}-driver`}
									name="driverName"
									type="text"
									required
									defaultValue={car?.driverName ?? ""}
									className="w-full px-4 py-2"
									placeholder="Johnny"
								/>
							</div>
							<div>
								<label
									htmlFor={`${formId}-car`}
									className="block text-sm mb-1"
								>
									Car Name (optional)
								</label>
								<input
									id={`${formId}-car`}
									name="carName"
									type="text"
									defaultValue={car?.carName ?? ""}
									className="w-full px-4 py-2"
									placeholder="Lightning Bolt"
								/>
							</div>
						</div>

						<div
							className={`grid grid-cols-1 gap-4 ${hasDivisions ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}
						>
							<div>
								<label
									htmlFor={`${formId}-number`}
									className="block text-sm mb-1"
								>
									Car Number
								</label>
								<input
									id={`${formId}-number`}
									name="carNumber"
									type="number"
									min="1"
									required
									defaultValue={car?.carNumber ?? ""}
									className="w-full px-4 py-2"
									placeholder="#"
								/>
							</div>
							<div>
								<label
									htmlFor={`${formId}-weight`}
									className="block text-sm mb-1"
								>
									Weight ({weightUnit})
								</label>
								<input
									id={`${formId}-weight`}
									name="weightOz"
									type="number"
									step="0.01"
									min="0"
									defaultValue={car?.weightOz ?? ""}
									className="w-full px-4 py-2"
									placeholder={weightUnit === "oz" ? "5.00" : "141.70"}
								/>
							</div>
							{hasDivisions && (
								<div>
									<label
										htmlFor={`${formId}-division`}
										className="block text-sm mb-1"
									>
										Division
									</label>
									<select
										id={`${formId}-division`}
										name="division"
										defaultValue={car?.division ?? ""}
										className="w-full px-4 py-2"
									>
										<option value="">None</option>
										{divisions.map((div) => (
											<option key={div} value={div}>
												{div}
											</option>
										))}
									</select>
								</div>
							)}
						</div>

						<div>
							<label
								htmlFor={`${formId}-photo`}
								className="block text-sm mb-1"
							>
								Car Photo
							</label>
							<input
								id={`${formId}-photo`}
								type="file"
								accept="image/*"
								onChange={(e) =>
									setPhotoFile(e.target.files?.[0] || null)
								}
								className="w-full px-4 py-2 bg-background border border-input rounded-lg text-foreground text-sm file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:bg-primary file:text-primary-foreground file:font-bold file:text-xs file:uppercase"
							/>
						</div>

						{error && (
							<p className="text-destructive text-sm">{error}</p>
						)}

						<div className="flex gap-3 pt-1">
							<button
								type="submit"
								disabled={loading}
								className="px-6 py-2 bg-primary hover:bg-accent disabled:opacity-50 text-primary-foreground font-bold rounded-lg transition-colors uppercase text-sm"
							>
								{loading
									? "Saving..."
									: mode === "add"
										? "Add Car"
										: "Save"}
							</button>
							<button
								type="button"
								onClick={onClose}
								className="px-6 py-2 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-lg transition-colors text-sm"
							>
								Cancel
							</button>
						</div>
					</form>
				</div>
			</div>
		</>
	);
}

// ── Import preview modal ────────────────────────────────────────

function ImportPreviewModal({
	rows,
	hasDivisions,
	importing,
	onClose,
	onApply,
}: {
	rows: ImportRow[];
	hasDivisions: boolean;
	importing: boolean;
	onClose: () => void;
	onApply: (rows: ImportRow[]) => void;
}) {
	const newCount = rows.filter((r) => r.status === "new").length;
	const updateCount = rows.filter((r) => r.status === "update").length;
	const unchangedCount = rows.filter((r) => r.status === "unchanged").length;
	const warningCount = rows.filter((r) => r.warnings.length > 0).length;
	const changeCount = newCount + updateCount;

	return (
		<>
			<div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
			<div className="fixed inset-0 z-50 flex items-center justify-center p-4">
				<div className="bg-card rounded-xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col">
					<div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
						<h2 className="text-lg font-bold text-foreground">
							Import Preview
						</h2>
						<button
							type="button"
							onClick={onClose}
							className="p-1 hover:bg-secondary rounded-lg transition-colors"
						>
							<X size={18} />
						</button>
					</div>

					<div className="px-5 py-3 border-b border-border shrink-0">
						<div className="flex items-center gap-4 text-sm">
							{newCount > 0 && (
								<span className="flex items-center gap-1.5">
									<span className="w-2 h-2 rounded-full bg-success" />
									{newCount} new
								</span>
							)}
							{updateCount > 0 && (
								<span className="flex items-center gap-1.5">
									<span className="w-2 h-2 rounded-full bg-amber-500" />
									{updateCount} update{updateCount !== 1 ? "s" : ""}
								</span>
							)}
							{unchangedCount > 0 && (
								<span className="flex items-center gap-1.5 text-muted-foreground">
									<span className="w-2 h-2 rounded-full bg-muted-foreground/40" />
									{unchangedCount} unchanged
								</span>
							)}
							{warningCount > 0 && (
								<span className="flex items-center gap-1.5 text-amber-600">
									<AlertTriangle size={12} />
									{warningCount} warning{warningCount !== 1 ? "s" : ""}
								</span>
							)}
						</div>
					</div>

					<div className="overflow-auto flex-1">
						<table className="w-full text-sm">
							<thead className="sticky top-0 bg-card">
								<tr className="border-b border-border">
									<th className="px-3 py-2 text-left text-xs font-bold text-muted-foreground uppercase">
										Status
									</th>
									<th className="px-3 py-2 text-left text-xs font-bold text-muted-foreground uppercase">
										#
									</th>
									<th className="px-3 py-2 text-left text-xs font-bold text-muted-foreground uppercase">
										Driver
									</th>
									<th className="px-3 py-2 text-left text-xs font-bold text-muted-foreground uppercase">
										Car Name
									</th>
									<th className="px-3 py-2 text-left text-xs font-bold text-muted-foreground uppercase">
										Weight
									</th>
									{hasDivisions && (
										<th className="px-3 py-2 text-left text-xs font-bold text-muted-foreground uppercase">
											Division
										</th>
									)}
									<th className="px-3 py-2 text-left text-xs font-bold text-muted-foreground uppercase">
										Checked In
									</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-border">
								{rows.map((row, i) => (
									<Fragment key={`import-${i}`}>
										<tr
											className={
												row.status === "new"
													? "bg-success/10"
													: row.status === "update"
														? "bg-amber-500/10"
														: ""
											}
										>
											<td className="px-3 py-2">
												<StatusBadge status={row.status} />
											</td>
											<DiffCell row={row} field="carNumber">
												{row.carNumber ?? "—"}
											</DiffCell>
											<DiffCell row={row} field="driverName">
												{row.driverName}
											</DiffCell>
											<DiffCell row={row} field="carName">
												{row.carName || (
													<span className="text-muted-foreground">—</span>
												)}
											</DiffCell>
											<DiffCell row={row} field="weightOz">
												{row.weightOz ?? (
													<span className="text-muted-foreground">—</span>
												)}
											</DiffCell>
											{hasDivisions && (
												<DiffCell row={row} field="division">
													{row.division || (
														<span className="text-muted-foreground">—</span>
													)}
												</DiffCell>
											)}
											<DiffCell row={row} field="checkedIn">
												{row.checkedIn ? "Yes" : "No"}
											</DiffCell>
										</tr>
										{row.warnings.length > 0 && (
											<tr className="bg-amber-500/5">
												<td
													colSpan={hasDivisions ? 7 : 6}
													className="px-3 py-1.5 text-xs text-amber-600"
												>
													<span className="flex items-center gap-1.5">
														<AlertTriangle size={12} />
														{row.warnings.join("; ")}
													</span>
												</td>
											</tr>
										)}
									</Fragment>
								))}
							</tbody>
						</table>
					</div>

					<div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-border shrink-0">
						<button
							type="button"
							onClick={onClose}
							className="px-4 py-2 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-lg transition-colors text-sm"
						>
							Cancel
						</button>
						<button
							type="button"
							onClick={() => onApply(rows)}
							disabled={changeCount === 0 || importing}
							className="px-4 py-2 bg-primary hover:bg-accent disabled:opacity-50 text-primary-foreground font-bold rounded-lg transition-colors text-sm uppercase"
						>
							{importing ? (
								<span className="flex items-center gap-2">
									<Loader2 size={14} className="animate-spin" />
									Applying...
								</span>
							) : changeCount === 0 ? (
								"No Changes"
							) : (
								`Apply ${changeCount} Change${changeCount !== 1 ? "s" : ""}`
							)}
						</button>
					</div>
				</div>
			</div>
		</>
	);
}

function DiffCell({
	row,
	field,
	children,
}: {
	row: ImportRow;
	field: string;
	children: React.ReactNode;
}) {
	const isChanged = row.status === "update" && row.changedFields.has(field);
	return (
		<td
			className={`px-3 py-2 ${isChanged ? "text-amber-600 dark:text-amber-400 font-bold" : "text-foreground"}`}
		>
			{children}
		</td>
	);
}

function StatusBadge({ status }: { status: ImportRow["status"] }) {
	switch (status) {
		case "new":
			return (
				<span className="inline-flex items-center px-1.5 py-0.5 bg-success/20 text-success text-xs font-bold rounded">
					New
				</span>
			);
		case "update":
			return (
				<span className="inline-flex items-center px-1.5 py-0.5 bg-amber-500/20 text-amber-600 dark:text-amber-400 text-xs font-bold rounded">
					Update
				</span>
			);
		case "unchanged":
			return (
				<span className="inline-flex items-center px-1.5 py-0.5 bg-muted text-muted-foreground text-xs font-bold rounded">
					No Change
				</span>
			);
	}
}
